
import { 
  Box, 
  IconButton, 
  Tooltip,
  useTheme
} from '@mui/material'
import type { ReactNode } from 'react'

export interface WaveTabButtonProps {
  /** Icon to display in the button */
  icon: ReactNode
  
  /** Label for tooltip and accessibility */
  label: string
  
  /** Whether this tab is currently active */
  active?: boolean
  
  /** Click handler */
  onClick?: () => void
  
  /** Whether the button is disabled */
  disabled?: boolean
}

/**
 * Individual tab button for the Wave sidebar tab bar
 * Displays an icon with tooltip and handles active/inactive states
 */
export function WaveTabButton({
  icon,
  label,
  active = false,
  onClick,
  disabled = false
}: WaveTabButtonProps) {
  const theme = useTheme()
  
  return (
    <Tooltip title={label} placement="right">
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {/* Active indicator bar */}
        {active && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 2,
              height: 20,
              backgroundColor: theme.palette.primary.main,
              borderRadius: '0 1px 1px 0'
            }}
          />
        )}
        
        <IconButton
          onClick={onClick}
          disabled={disabled}
          size="medium"
          sx={{
            width: 48,
            height: 48,
            color: active 
              ? theme.palette.primary.main 
              : theme.palette.text.secondary,
            backgroundColor: active 
              ? theme.palette.action.selected 
              : 'transparent',
            '&:hover': {
              backgroundColor: active 
                ? theme.palette.action.selected 
                : theme.palette.action.hover,
              color: active 
                ? theme.palette.primary.main 
                : theme.palette.text.primary
            },
            '&:disabled': {
              color: theme.palette.action.disabled
            },
            transition: theme.transitions.create(['color', 'background-color'], {
              duration: theme.transitions.duration.short
            })
          }}
          aria-label={label}
          aria-pressed={active}
        >
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  )
}
